import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check } from "lucide-react";
import type { InterestQuestion } from "@omina/core";

import { useGoogleStatus, useInterests, useSaveInterests } from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * Interests — step 3, and now the one screen everybody sees.
 *
 * It used to be the consolation prize for skipping Google: connectors went
 * straight to curation, skippers came here. That made one question produce two
 * different products, and it meant the stated-preference column only ever
 * existed for founders whose calendar Omina could not read — so the founders
 * with the most signal had the least said about what they wanted.
 *
 * Now every founder answers, and the fork moved to the end of this screen,
 * where it belongs: curation is offered to whoever actually has ninety days of
 * rooms to curate, and skipped for whoever does not.
 *
 * Five questions, eight options each, three picks. The cap is the design:
 * "everything matters" carries no information, and an unlimited multi-select
 * collects exactly that every single time. Forcing three forces a ranking.
 *
 * **One question at a time.** All of them on one page was a scroll of forty
 * chips, and forty chips is a form — she skims it, picks whatever is nearest
 * the top of each row, and the ranking the cap was supposed to force never
 * happens. One question fills the screen, so choosing three out of eight is
 * the only thing to do on it.
 *
 * **Everything stored from here is her stated preference, and it is the weaker
 * signal.** It lives in its own column rather than being merged into the
 * profile's buyer fields, so that when real outcomes arrive nothing has to
 * guess which of the two it is reading. See `profiles.interests`.
 */
export const Route = createFileRoute("/interests")({
  component: Interests,
});

function Interests() {
  const interests = useInterests();

  if (interests.isPending) {
    return (
      <div className="intwrap">
        <p className="lede">One moment…</p>
      </div>
    );
  }

  if (interests.isError) {
    return (
      <div className="intwrap">
        <h1 className="tk">That did not load</h1>
        <p className="lede">{interests.error.message}</p>
      </div>
    );
  }

  // The form is its own component so its state can seed from the loaded
  // answers exactly once, in useState's initializer — a founder mid-question
  // must never have her picks replaced by a background refetch.
  return <InterestsForm data={interests.data} />;
}

function InterestsForm({
  data,
}: {
  data: { questions: InterestQuestion[]; picks: number; answers: Record<string, string[]> | null };
}) {
  const save = useSaveInterests();
  const navigate = useNavigate();

  /**
   * Asked here rather than passed in from sign-in.
   *
   * The next screen depends on whether Google actually granted the calendar,
   * and the only thing that knows is the grant. A query parameter set by the
   * button she pressed would be a second copy of that fact, free to disagree
   * with it — and it disagrees in exactly the case that matters, when consent
   * is abandoned on Google's own screen and she arrives back here having
   * pressed "Allow" and granted nothing.
   *
   * Fetched on mount so the answer is already in hand by the time she finishes
   * the questions, rather than making her wait on it at the end.
   */
  const google = useGoogleStatus();

  const picks = data.picks ?? 3;

  /**
   * Seeded from what the server holds, which is `{}` on a first pass.
   *
   * This used to start blank unconditionally, on the reasoning that a founder
   * deciding should not arrive to chips already lit — and on a first pass she
   * still does not, by construction, because there is nothing to seed. But
   * `/profile` links here as the place to edit her interests, and saving
   * replaces the whole map: a returning founder who came to add one answer
   * was silently wiping the other four. Seeding keeps both regimes honest —
   * blank when she has said nothing, hers when she has.
   *
   * Clamped to `picks` per question defensively; the server enforces the cap
   * on write, so anything longer here is a stale row, not a valid answer.
   */
  const [answers, setAnswers] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(
      Object.entries(data.answers ?? {}).map(([q, picked]) => [q, picked.slice(0, picks)]),
    ),
  );
  const [step, setStep] = useState(0);

  /**
   * Tapping a chip.
   *
   * Full at three, and a tap on a fourth does nothing rather than silently
   * evicting the oldest — a chip that vanishes because of something she chose
   * three taps ago is the interface making a decision she did not see. She
   * deselects to change her mind.
   *
   * Updated from the previous state rather than from `answers` in scope. Two
   * taps inside one render batch both read the same closed-over value, so the
   * second overwrote the first and a fast pair of taps landed as one pick —
   * invisible, because the chip she tapped second lit up exactly as expected
   * and the one before it quietly went out.
   */
  function toggle(questionId: string, optionId: string) {
    setAnswers((prev) => {
      const current = prev[questionId] ?? [];
      const has = current.includes(optionId);
      if (!has && current.length >= picks) return prev;
      return {
        ...prev,
        [questionId]: has ? current.filter((id) => id !== optionId) : [...current, optionId],
      };
    });
  }

  const questions = data.questions;
  const q = questions[step];
  if (!q) return null;

  const chosen = answers[q.id] ?? [];
  const full = chosen.length >= picks;
  const last = step === questions.length - 1;

  /**
   * The end of the flow, decided by the grant rather than by the button she
   * pressed two screens ago.
   *
   * A founder with the calendar has ninety days of rooms Omina can read and no
   * idea what any of them meant, so curation is the next thing worth her time.
   * Without it there is nothing to curate, and the honest move is to go
   * straight on to the goal — not to show her an empty curation screen
   * explaining why it is empty.
   *
   * Unknown resolves to "no". A failed status call sending a connected founder
   * to the goal costs her one screen she can still reach later; the other way
   * round lands her on a curation queue that will never fill.
   */
  function finish() {
    save.mutate(answers, {
      onSuccess: () => {
        const canCurate = google.data?.scopes.calendar === true;
        void navigate({ to: canCurate ? "/curation" : "/path" });
      },
    });
  }

  return (
    <div className="intwrap">
      <div className="eyebrow">Getting started</div>
      {/* Plain about why Omina is asking: the answers are used on her behalf.
          "A few questions a good friend would ask" was tried and cut — it
          described the product's intent instead of hers. */}
      <h1 className="tk tk-want">We need to understand your preferences</h1>

      {/* Where she is, as a fact and as a shape. The bar is the one that gets
          read — a founder on question two wants to know how much is left, not
          which ordinal she is on. */}
      <div className="intprog">
        <div className="intprogbar" aria-hidden>
          <span style={{ width: `${((step + 1) / questions.length) * 100}%` }} />
        </div>
        <span className="inthint">
          {step + 1} of {questions.length}
        </span>
      </div>

      {/* `key` on the question, so React rebuilds the block rather than
          re-labelling the old chips. Without it a chip that was on stays
          visually on for a frame under the next question's label. */}
      <section className="intq intq-solo" key={q.id}>
        <h2 className="intprompt">{q.prompt}</h2>
        {/* The question's own hint when it has one — most of them already say
            how to choose, and appending the generic rule to it produced "pick
            the three you would cross town for · pick up to 3". The rule is
            only worth printing where nothing else says it. */}
        <p className="inthint">{q.hint ?? `Pick up to ${picks}, or skip if none fit.`}</p>

        <ul className="intchips">
          {q.options.map((o) => {
            const on = chosen.includes(o.id);
            return (
              <li key={o.id}>
                <button
                  type="button"
                  className={cn("intchip", on && "on")}
                  aria-pressed={on}
                  // Full means the unpicked ones stop responding. Dimming
                  // them without disabling would be a button that looks
                  // available and does nothing.
                  disabled={!on && full}
                  onClick={() => toggle(q.id, o.id)}
                >
                  {on ? <Check size={13} aria-hidden /> : <span className="intplus">+</span>}
                  {o.label}
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <div className="intsave">
        {/* Back only once there is a back. On the first question it would be a
            control that leaves the flow. */}
        {step > 0 ? (
          <button type="button" className="authback" onClick={() => setStep(step - 1)}>
            <ArrowLeft size={13} aria-hidden />
            Back
          </button>
        ) : (
          <span />
        )}

        {/* Never disabled on a picked-nothing question: skipping is a real
            answer here, and a founder none of whose eight options fit should
            not be held on the screen by a dead button. */}
        <button
          type="button"
          className={cn("btn", save.isPending && "loading")}
          // On the last question only, the grant has to be in hand — otherwise
          // the fork below reads `undefined` and skips curation for a founder
          // who has a calendar. In practice this never blocks: the query is
          // fired on mount and four questions is an age.
          disabled={save.isPending || (last && google.isPending)}
          onClick={() => (last ? finish() : setStep(step + 1))}
        >
          <span className="sp" aria-hidden />
          {save.isPending ? "Saving…" : last ? "Continue" : "Next"}
        </button>
      </div>

      {save.isError && <div className="danger">{save.error.message}</div>}
    </div>
  );
}
